
import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useOfferPageData } from '@/hooks/useOfferPageData';
import PaymentPageSkeleton from '@/components/payment/PaymentPageSkeleton';
import PaymentNotFound from '@/components/payment/PaymentNotFound';
import OfferHeader from '@/components/payment/OfferHeader';
import OfferFooter from '@/components/payment/OfferFooter';
import OfferProductList from '@/components/offer/OfferProductList';
import OfferPageContainer from '@/components/offer/OfferPageContainer';
import NoProductsWarning from '@/components/offer/NoProductsWarning';
import CheckoutContent from '@/components/offer/CheckoutContent';

// IMPORTANT: 🚨 DO NOT MODIFY THIS COMPONENT 🚨
// This component is a critical part of the offer page rendering process.
// Any changes to this file MUST be carefully reviewed and approved.
// Unauthorized modifications may break core functionality.

const OfferPage: React.FC = () => {
  // PRESERVATION WARNING: Maintain existing logic and structure
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // Extract external user_id and product_id from URL query params for external integrations
  const externalUserId = searchParams.get("user_id");
  const externalProductId = searchParams.get("product_id");
  const {
    offerPage,
    products,
    offerProducts,
    selectedProductIds,
    loading,
    getSelectedProducts,
    rawProductIds
  } = useOfferPageData(id || null);
  
  // PRESERVATION NOTE: Rendering logic should not be altered
  if (loading) {
    return <PaymentPageSkeleton />;
  }
  
  if (!offerPage) {
    return <PaymentNotFound />;
  }
  
  const selectedProducts = getSelectedProducts();
  const primaryProduct = selectedProducts.length > 0 ? selectedProducts[0] : null;
  
  return (
    // CRITICAL: Maintain exact structure and component composition
    <OfferPageContainer>
      <OfferHeader 
        imagePath={offerPage.imagePath || offerPage.image_path}
        heading={offerPage.heading || offerPage.title}
        description={offerPage.subheading|| 'No description available.'}
        title={offerPage.title}
      />

      {/* PRESERVATION WARNING: Do not modify product list rendering */}
      <OfferProductList
        offerProducts={offerProducts}
        selectedProductIds={selectedProductIds}
      />

      {offerProducts.length === 0 && (
        <NoProductsWarning 
          offerId={offerPage.id.substring(0, 8)} 
          productCount={offerPage.products ? offerPage.products.length : 0}
          rawProductIds={rawProductIds}
        />
      )}

      {/* Single Column Layout - Payment Section */}
      {offerProducts.length > 0 && selectedProductIds.length > 0 && (
        <CheckoutContent
          selectedProductIds={selectedProductIds}
          selectedProducts={selectedProducts}
          primaryProduct={primaryProduct}
          externalUserId={externalUserId || undefined}
          externalProductId={externalProductId || undefined}
        />
      )}

      {/* Footer must be preserved */}
      <OfferFooter />
    </OfferPageContainer>
  );
};

export default OfferPage;